import React, { useEffect, useState, useReducer, Suspense } from 'react'
import { Helmet } from 'react-helmet-async'
import { Route, Switch } from 'react-router'
import { Redirect, useLocation } from 'react-router-dom'
import Cookies from 'universal-cookie'
import { ThemeProvider } from 'styled-components'

// Styling
import GlobalStyle from './styles'

// Utils
import { isClient } from './utils/getRenderPlatform'
import { IRoute, IRoutes, paths } from './constants/routes'
import {
  AppDispatchCtx,
  appReducer,
  AppStateCtx,
  DEFAULT_APP_STATE,
  useAppDispatch,
  useAppState,
} from './context/AppContext'
import AsyncInitializer from './context/AsyncInitializer'
import { LayoverStackProvider } from './context/LayoverStackContext'
import { isModuleEnabled } from './hooks/useModuleAccess'
import { cacheCompanyColors, getInitialTheme } from './utils/companyThemeCache'

// Components
import DialogScreen from './components/DialogScreen'
import Notification from './components/Notification'
import RequestModals from './components/RequestModals'

// Hooks
import useApplyCompanyTheme from './hooks/useApplyCompanyTheme'
import useApplyCompanyLanguage from './hooks/useApplyCompanyLanguage'

// Types
interface IProps {
  routes: IRoutes
}

const VERSION = process.env.VERSION || 'N.A.'
const COMMIT = process.env.COMMIT || 'N.A.'

/* istanbul ignore next */
if (process.env.NODE_ENV !== 'test' && isClient()) {
  console.info(`
  ░██████╗██╗░░░██╗███╗░░░███╗███╗░░░███╗
  ██╔════╝██║░░░██║████╗░████║████╗░████║
  ╚█████╗░██║░░░██║██╔████╔██║██╔████╔██║
  ░╚═══██╗██║░░░██║██║╚██╔╝██║██║╚██╔╝██║
  ██████╔╝╚██████╔╝██║░╚═╝░██║██║░╚═╝░██║
  ╚═════╝░░╚═════╝░╚═╝░░░░░╚═╝╚═╝░░░░░╚═╝
version: ${VERSION}
commit: ${COMMIT}
  `)
}

export const AppRoute = (route: IRoute) => {
  const { search, pathname, hash } = useLocation()
  const { company } = useAppState()

  const [currentPath, setCurrentPath] = useState<string>()
  const [previousPath, setPreviousPath] = useState<string>()

  useEffect(() => {
    if (!currentPath) setCurrentPath(pathname)
    if (pathname !== currentPath) {
      setPreviousPath(currentPath)
      setCurrentPath(pathname)
    }
  }, [pathname])

  useEffect(() => {
    // A link to a section (#notifications from a mail): the page scrolls to it
    if (!window || hash) return
    if (
      currentPath &&
      previousPath &&
      currentPath.split('/')[1] === previousPath.split('/')[1]
      // prevent window to scroll up, if just navigating/switching between tabs
    ) {
      if (previousPath.split('/').length > 2) {
        const pageWrapper = document.getElementById('intoView')
        if (!pageWrapper || !window) return
        window.scrollTo({
          top: pageWrapper?.getBoundingClientRect().top + window.scrollY - 88,
          behavior: 'smooth',
        })
      }
      return
    }
    window.scrollTo(0, 0)
  }, [currentPath, previousPath])

  const redirectAfterLogin = () => {
    const params = new URLSearchParams(search)
    // Back from Google's login page the redirect comes back as `state` (SignIn)
    const redirectTo = params.get('redirectTo') || params.get('state')
    if (redirectTo) {
      return redirectTo
    } else {
      return paths.personalProfile
    }
  }

  return (
    <Suspense fallback={<route.fallbackComponent />}>
      <Route
        path={route.path}
        render={(props) => {
          // Module gate (company entitlement axis): when authenticated and the
          // route's module is disabled for the company, redirect before the page
          // mounts — no wasted data-fetch, no flash, rules-of-hooks-safe. Absent
          // module flag = enabled (loading state keeps everything visible).
          // NOTE: distinct from a future role/permission denial — keep a separate
          // surface there (this stays the entitlement bounce).
          if (
            route.appRefreshToken &&
            route.requiredModule &&
            !isModuleEnabled(company, route.requiredModule)
          ) {
            return <Redirect to="/" />
          }
          return (
            // Redirect to login page if user is not logged in yet
            <>
              <route.component {...props} />
              {!route.appRefreshToken &&
              (route.path.includes('external-feedback') ||
                route.public) ? null : (
                <>
                  {!route.appRefreshToken && route.path !== paths.signIn && (
                    <Redirect
                      to={`${paths.signIn}?redirectTo=${encodeURIComponent(
                        pathname + search + hash
                      )}`}
                    />
                  )}
                  {route.path === paths.signIn && route.appRefreshToken && (
                    <Redirect to={redirectAfterLogin()} />
                  )}
                </>
              )}
            </>
          )
        }}
      />
    </Suspense>
  )
}

// Holds everything that consumes app state/dispatch. Rendered inside the
// providers (see App below) so context hooks like useApplyCompanyTheme resolve —
// App itself renders the providers and therefore cannot consume them.
export const AppContent = ({ routes }: IProps) => {
  const cookies = new Cookies()

  const state = useAppState()
  const dispatch = useAppDispatch()

  const refreshToken = cookies.get('refresh_token') || undefined

  const onSubmitPopup = () => {
    dispatch({
      type: 'updateAlertPopup',
      payload: undefined,
    })
  }

  const notification = state.notification

  useEffect(() => {
    if (!notification || !notification.show) return
    setTimeout(() => {
      dispatch({
        type: 'updateNotification',
        payload: {
          show: false,
          label: '',
        },
      })
    }, 4000)
  }, [notification])

  useApplyCompanyTheme(state.company?.companyColors)
  useEffect(() => {
    const companyColors = state.company?.companyColors
    if (companyColors) cacheCompanyColors(companyColors)
  }, [state.company?.companyColors])
  useApplyCompanyLanguage(state.company?.languageConfig)

  return (
    <ThemeProvider theme={state.theme}>
      <LayoverStackProvider>
        <Helmet titleTemplate="%s – SUMM" />
        {refreshToken && <AsyncInitializer />}
        {state.notification && state.notification.show && (
          <Notification label={state.notification.label} />
        )}
        {state.alertPopup && (
          <DialogScreen
            content={state.alertPopup}
            onSubmit={state.alertPopup?.onSubmit || onSubmitPopup}
            onDismiss={
              state.alertPopup.dismissButton || state.alertPopup.onDismiss
                ? state.alertPopup.onDismiss || onSubmitPopup
                : !(state.alertPopup.onSubmit || state.alertPopup.actionButton)
                ? onSubmitPopup
                : undefined
            }
            actionButton={
              state.alertPopup?.actionButton || 'general.actions.ok'
            }
          />
        )}
        <RequestModals />
        <Switch>
          {routes.map((route: IRoute, i: number) => {
            // Only pass allowed route props to Route component
            const { ...routeProps } = route
            return (
              <AppRoute
                key={i}
                appRefreshToken={refreshToken}
                {...routeProps}
              />
            )
          })}
        </Switch>
        <GlobalStyle />
      </LayoverStackProvider>
    </ThemeProvider>
  )
}

// Owns the app reducer and exposes it via the state/dispatch providers, then
// renders AppContent inside them.
const App = ({ routes }: IProps) => {
  const [state, dispatch] = useReducer(
    appReducer,
    DEFAULT_APP_STATE,
    (initial) => ({
      ...initial,
      theme: getInitialTheme(),
    })
  )

  return (
    <AppStateCtx.Provider value={state}>
      <AppDispatchCtx.Provider value={dispatch}>
        <AppContent routes={routes} />
      </AppDispatchCtx.Provider>
    </AppStateCtx.Provider>
  )
}

export default App
